import React, { useState, useEffect } from 'react';
import { parse, isValid, isBefore } from 'date-fns';
import './Footer.scss';

interface FooterProps {
  startDate: string;
  endDate: string;
  onApply: () => void;
  onCancel: () => void;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
}

const Footer: React.FC<FooterProps> = ({
  startDate,
  endDate,
  onApply,
  onCancel,
  onStartDateChange,
  onEndDateChange,
}) => {
  const [startInputValue, setStartInputValue] = useState(startDate);
  const [endInputValue, setEndInputValue] = useState(endDate);

  useEffect(() => {
    setStartInputValue(startDate);
  }, [startDate]);

  useEffect(() => {
    setEndInputValue(endDate);
  }, [endDate]);

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStartInputValue(value);

    try {
      // Check if the input matches the pattern "MMM d, yyyy" or is in the process of being typed
      const datePattern = /^[A-Za-z]{3}\s\d{1,2},(\s\d{0,4})?$/;

      if (datePattern.test(value)) {
        // If the year part is missing or incomplete, don't try to parse it yet
        if (!value.includes(',') || value.split(',')[1].trim() === '' || value.split(',')[1].trim().length < 4) {
          return;
        }

        // Try to parse the date in format "MMM d, yyyy"
        const parsedDate = parse(value, 'MMM d, yyyy', new Date());

        if (isValid(parsedDate)) {
          onStartDateChange(parsedDate);
        }
      }
    } catch (error) {
      // Invalid date format, do nothing
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEndInputValue(value);

    try {
      // Check if the input matches the pattern "MMM d, yyyy" or is in the process of being typed
      const datePattern = /^[A-Za-z]{3}\s\d{1,2},(\s\d{0,4})?$/;

      if (datePattern.test(value)) {
        // If the year part is missing or incomplete, don't try to parse it yet
        if (!value.includes(',') || value.split(',')[1].trim() === '' || value.split(',')[1].trim().length < 4) {
          return;
        }

        // Try to parse the date in format "MMM d, yyyy"
        const parsedDate = parse(value, 'MMM d, yyyy', new Date());

        if (isValid(parsedDate)) {
          // Check if the end date is before the start date
          const startDateObj = parse(startInputValue, 'MMM d, yyyy', new Date());

          if (isValid(startDateObj) && isBefore(parsedDate, startDateObj)) {
            // If end date is before start date, set both to the start date
            onStartDateChange(startDateObj);
            onEndDateChange(startDateObj);
          } else {
            onEndDateChange(parsedDate);
          }
        }
      }
    } catch (error) {
      // Invalid date format, do nothing
    }
  };

  return (
    <footer className='footer' style={{ border: 'none' }}>
      <div className='date-inputs'>
        <div className='date-input-wrapper'>
          <input
            type='text'
            value={startInputValue}
            onChange={handleStartDateChange}
            className='date-input'
            placeholder='MMM d, yyyy'
          />
        </div>
        {/* <span className='date-separator'>–</span> */}
        <div className='date-input-wrapper'>
          <input
            type='text'
            value={endInputValue}
            onChange={handleEndDateChange}
            className='date-input'
            placeholder='MMM d, yyyy'
          />
        </div>
      </div>
      <div className='footer-buttons'>
        <button 
          data-analytics-id='date-range-footer-cancel-btn' 
          className='cancel-button' 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }}
        >
          <span className='button-text'>Cancel</span>
        </button>
        <button 
          data-analytics-id='date-range-footer-apply-btn' 
          className='apply-button' 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onApply();
          }}
        >
          <span className='button-text'>Apply</span>
        </button>
      </div>
    </footer>
  );
};

export default Footer;
